// Report text helpers shared by the documents AND the page planner: the
// period / generation lines and the truncation note. Pure on purpose (no
// react-pdf), because the planner measures the note's height from its real
// text and the planner runs in the main bundle for the row picker.
import { translate, translateWithVars } from "shared/languages";
import { localTzDate, tzDate } from "shared/utils/dayjs";

// Every report prints "Periode: <range>" and the generation stamp.
export const periodLineOf = (label: string) => `${translate("pdf.period_prefix")}: ${label}`;
export const generatedStampLine = () =>
  `${translate("pdf.generated_prefix")}: ${localTzDate(new Date().toISOString()).format("DD MMM YYYY HH.mm")} WIB`;

// Pembelian's "what changed, when" sentence, shared by the screen slot above
// its table and the PDF note (through taxBasisNote below), so both print the
// same words and the page planner measures the real text. Dated whenever
// the server's change log holds a tax change inside the period ("Pengaturan
// pajak berubah pada 12 Okt 2026."), which includes tax switched on or off,
// the change the confirm dialog promises to mark and the one the Pajak
// column cannot date on its own; undated when the period's taxed rows follow
// more than one rule with no logged change behind it (the change predates
// the log, or came from an import); nothing otherwise. Dates are deduped on
// the Jakarta day, so a mode and a rate saved in one batch print once.
export const taxChangeSentence = (
  tax: { mixed?: boolean; changes?: { at: string }[] } | undefined,
): string | undefined => {
  if (!tax) return undefined;
  const days = new Map<string, string>();
  for (const c of tax.changes ?? []) {
    const key = tzDate(c.at).format("YYYY-MM-DD");
    if (!days.has(key)) days.set(key, localTzDate(c.at).format("D MMM YYYY"));
  }
  if (days.size > 0) {
    return translateWithVars("analyticsPage.sales.tax_changed_on", {
      dates: Array.from(days.values()).join(", "),
    });
  }
  return tax.mixed ? translate("analyticsPage.sales.mixed_tax_note") : undefined;
};

// The basis line a report prints under its table when the figures it prints
// include a taxed sale: Harga Jual and every discount are before tax
// (util/sale-breakdown.ts on the server), the way the Monthly Statement
// prints them. A workbook says the same on its cover sheet; the screen says
// it in tooltips. Pembelian adds the mixed-settings sentence when the period
// spans more than one tax rule. Undefined when nothing printed was taxed, so
// an untaxed studio's export carries no tax talk at all.
export const taxBasisNote = (
  data: {
    tax?: { any?: boolean; mixed?: boolean; changes?: { at: string }[] };
    money_hidden?: boolean;
  },
  kind: "purchases" | "discounts" | "earned" | "earned_cash" | "cash",
): string | undefined => {
  // Pembelian's change sentence stands on its own when nothing listed was
  // taxed (tax switched on at the end of a period with no sale after it):
  // the screen prints it above the table, so the PDF prints it too.
  const changed = kind === "purchases" ? taxChangeSentence(data.tax) : undefined;
  if (!data.tax?.any) return changed;
  // A financially restricted Admin reads these reports with their money
  // columns dropped. A basis line describing a column that is not on the
  // page is worse than no line, so it is withheld with the column. (The
  // package overview blanks its money fields without setting this flag at
  // all, which is why the server also switches tax.any off there.)
  if (data.money_hidden && kind !== "discounts") return undefined;
  const key =
    kind === "purchases"
      ? "pdf.purchases_basis_note"
      : kind === "discounts"
        ? "pdf.discount_basis_note"
        : kind === "cash"
          ? "pdf.cash_basis_note"
          : "pdf.earned_basis_note";
  const basis =
    kind === "earned_cash"
      ? `${translate("pdf.earned_basis_note")} ${translate("pdf.cash_basis_note")}`
      : translate(key);
  return changed ? `${basis} ${changed}` : basis;
};

// Several notes on one line, in order, skipping the absent ones.
export const joinNotes = (...parts: (string | undefined)[]): string | undefined => {
  const present = parts.filter((p): p is string => !!p);
  return present.length ? present.join(" ") : undefined;
};

// The truncation note, with the data-correctness guard: when the loaded
// payload is itself server-truncated AND the admin sorted by a non-default
// column, the top-N is drawn from the loaded rows only.
export const truncationNote = ({
  shown,
  total,
  noun,
  loaded,
  sortedOffDefault,
}: {
  shown: number;
  total: number;
  noun: string;
  loaded: number;
  sortedOffDefault: boolean;
}): string | undefined => {
  if (shown >= total) return undefined;
  const guard =
    loaded < total && sortedOffDefault
      ? ` ${translateWithVars("pdf.sorted_from_loaded", { loaded })}`
      : "";
  return `${translateWithVars("pdf.truncation", { shown, total, noun })}${guard}`;
};
